import "./styles.scss";

import moment from "moment";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";

import DetailsCard from "components/DetailsCard";
import AnimatedGradient from "components/AnimatedGradient";

import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";

import gradient_props from "libs/gradient_props";

const mainClass = "dashboard-client-usage-chart";
const maxExceedingTimeMinutes = 8 * 60;

const UsageChart = ({ data }) => {
  const { t } = useTranslation();
  const { theme } = useApp();

  var currentDate = new Date();
  var currentMonth = currentDate.getMonth() + 1;
  var currentYear = currentDate.getFullYear();
  var lastDayOfMonth = moment().endOf("month").date();

  const toNumber = (value) => {
    const serializedValue =
      value?.$numberDecimal ??
      value?.$numberDouble ??
      value?.$numberInt ??
      value?.$numberLong ??
      value?.value ??
      value;

    if (serializedValue == null || serializedValue === "") return null;

    const numericValue = Number(serializedValue);

    if (Number.isFinite(numericValue)) return numericValue;

    const parsedValue = Number.parseFloat(value?.toString?.());

    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const availableInPlan = toNumber(data?.time_used?.available_in_plan);
  const timeUsedValue = toNumber(data?.time_used?.time_used);
  const timeLeftValue = toNumber(data?.time_used?.time_left);
  const timeExceedingPlanValue = toNumber(
    data?.time_used?.time_exceeding_plan
  );
  const percentageOfPlan = (value) => {
    if (availableInPlan === null || availableInPlan <= 0 || value === null) {
      return 0;
    }

    return Math.min(100, Math.max(0, (value / availableInPlan) * 100));
  };
  const formatTime = (value) =>
    value === null ? "-" : toHoursAndMinutes(value)?.formatted;

  const timeUsed = percentageOfPlan(timeUsedValue);
  const timeLeft = percentageOfPlan(timeLeftValue);
  const timeExceedingPlan =
    timeExceedingPlanValue === null
      ? 0
      : Math.min(
          100,
          Math.max(0, (timeExceedingPlanValue / maxExceedingTimeMinutes) * 100)
        );

  const time_data_list = [
    {
      label: t("Time available in your plan"),
      percentage: percentageOfPlan(availableInPlan),
      value: formatTime(availableInPlan),
      gradient: gradient_props.green,
    },
    {
      label: t("Time used"),
      percentage: timeUsed,
      value: formatTime(timeUsedValue),
      gradient: gradient_props.green,
    },
    {
      label: t("Time left"),
      percentage: timeLeft,
      value: availableInPlan === null ? "-" : formatTime(timeLeftValue),
      gradient: gradient_props.blue,
    },
    {
      color: "red",
      label: t("Time exceeding plan"),
      percentage: timeExceedingPlan,
      value: formatTime(timeExceedingPlanValue),
      gradient: gradient_props.red,
      extra_link: {
        label: t("Show"),
        href: `/tasks?time_over=true&start_due_date=${currentYear}-${currentMonth}-01&end_due_date=${currentYear}-${currentMonth}-${lastDayOfMonth}`,
      },
    },
  ];

  return (
    <DetailsCard
      title={t("Development work")}
      description={t(
        "See how much time you’ve used for development work on your site this month."
      )}
      action={{
        label: t("See more"),
        href: `/tasks?time_over=true&start_due_date=${currentYear}-${currentMonth}-01&end_due_date=${currentYear}-${currentMonth}-${lastDayOfMonth}`,
      }}
    >
      <div className={mainClass}>
        {time_data_list?.map((item, index) => (
          <div
            key={index}
            className={classnames(`${mainClass}__item`, {
              [`${mainClass}__item--${item?.color}`]: item?.color,
            })}
          >
            <div className={`${mainClass}__item__chart`}>
              <div
                className={`${mainClass}__item__chart__bar`}
                style={{ height: `${item?.percentage}%` }}
              >
                <div className={`${mainClass}__item__chart__bar__gradient`}>
                  <AnimatedGradient
                    id={`usage-gradient-${index}`}
                    time={item.gradient.time}
                    seed={item.gradient.seed}
                    colors={item.gradient.colors[theme]}
                  />
                </div>
              </div>
            </div>
            <div className={`${mainClass}__item__text`}>
              <span>{item?.label}:</span>
              <span>{item?.value}</span>
            </div>
          </div>
        ))}
      </div>
    </DetailsCard>
  );
};

export default UsageChart;
